import { queryClient } from "../main";

/**
 * Service to handle synchronization across different components
 * Provides methods to invalidate and refresh queries when data changes
 */
class SyncService {
  /**
   * Invalidate all queries related to uploads
   */
  static invalidateUploads() {
    queryClient.invalidateQueries({ queryKey: ["uploads"] });
    queryClient.invalidateQueries({ queryKey: ["upload"] });
  }

  /**
   * Invalidate all queries related to databases
   */
  static invalidateDatabases() {
    queryClient.invalidateQueries({ queryKey: ["databases"] });
    queryClient.invalidateQueries({ queryKey: ["databases-dropdown"] });
    queryClient.invalidateQueries({ queryKey: ["database"] });
    queryClient.invalidateQueries({ queryKey: ["tables"] });
    queryClient.invalidateQueries({ queryKey: ["columns"] });
  }

  /**
   * Invalidate all queries related to dashboard statistics
   */
  static invalidateDashboard() {
    queryClient.invalidateQueries({ queryKey: ["dashboard-overview"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-statistics"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-activity"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-health"] });
  }

  /**
   * Invalidate all queries related to categories
   */
  static invalidateCategories() {
    queryClient.invalidateQueries({ queryKey: ["categories"] });
  }

  /**
   * Comprehensive sync after upload operations
   */
  static syncAfterUpload() {
    this.invalidateUploads();
    this.invalidateDatabases();
    this.invalidateDashboard();
  }

  /**
   * Comprehensive sync after database operations
   */
  static syncAfterDatabaseChange() {
    this.invalidateDatabases();
    this.invalidateUploads();
    this.invalidateDashboard();
  }

  /**
   * Comprehensive sync after upload deletion
   */
  static syncAfterUploadDeletion() {
    this.invalidateUploads();
    this.invalidateDashboard();
    // Also refresh databases as statistics might have changed
    this.invalidateDatabases();
  }

  /**
   * Sync everything - use sparingly for major operations
   */
  static syncAll() {
    this.invalidateUploads();
    this.invalidateDatabases();
    this.invalidateDashboard();
    this.invalidateCategories();
  }

  /**
   * Auto-refresh queries with a delay to allow backend processing
   * Enhanced with error handling and cleanup
   */
  static async delayedSync(syncFunction, delay = 1000) {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(async () => {
        try {
          await syncFunction();
          resolve();
        } catch (error) {
          console.error("Delayed sync failed:", error);
          reject(error);
        }
      }, delay);

      // Store timeout ID for potential cleanup
      if (!this._timeouts) this._timeouts = new Set();
      this._timeouts.add(timeoutId);

      // Clean up after execution
      setTimeout(() => {
        this._timeouts?.delete(timeoutId);
      }, delay + 100);
    });
  }

  /**
   * Set up periodic sync for real-time updates with enhanced error handling
   * @param {number} interval - Interval in milliseconds (default: 30 seconds)
   * @param {Object} options - Configuration options
   */
  static startPeriodicSync(interval = 30000, options = {}) {
    const { onError, maxRetries = 3 } = options;
    let retryCount = 0;

    const syncWithRetry = async () => {
      try {
        // Only sync dashboard statistics periodically to avoid overwhelming the server
        await this.invalidateDashboard();
        retryCount = 0; // Reset retry count on success
      } catch (error) {
        console.error("Periodic sync failed:", error);
        retryCount++;

        if (retryCount >= maxRetries) {
          console.error(
            `Periodic sync failed ${maxRetries} times, stopping...`
          );
          if (onError) onError(error);
          return;
        }

        // Exponential backoff for retries
        setTimeout(() => syncWithRetry(), Math.pow(2, retryCount) * 1000);
      }
    };

    return setInterval(syncWithRetry, interval);
  }

  /**
   * Stop periodic sync
   */
  static stopPeriodicSync(intervalId) {
    if (intervalId) {
      clearInterval(intervalId);
    }
  }

  /**
   * Force refresh specific query
   */
  static async forceRefresh(queryKey) {
    await queryClient.invalidateQueries({ queryKey });
    return queryClient.refetchQueries({ queryKey });
  }

  /**
   * Get current cache data for debugging
   */
  static getCacheData(queryKey) {
    return queryClient.getQueryData(queryKey);
  }

  /**
   * Clear all cache data with cleanup
   */
  static clearAllCache() {
    // Clear any pending timeouts
    if (this._timeouts) {
      this._timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      this._timeouts.clear();
    }

    queryClient.clear();
  }

  /**
   * Cleanup method to be called on component unmount or app shutdown
   */
  static cleanup() {
    this.clearAllCache();

    // Clear any active intervals
    if (this._intervals) {
      this._intervals.forEach((intervalId) => clearInterval(intervalId));
      this._intervals.clear();
    }
  }

  /**
   * Enhanced invalidation with selective refresh
   * Only refreshes queries that are currently being observed
   */
  static async smartInvalidate(queryKey, options = {}) {
    const { forceRefresh = false, staleTime = 0 } = options;

    // Check if query is currently being observed
    const queryCache = queryClient.getQueryCache();
    const queries = queryCache.findAll({ queryKey });
    const hasObservers = queries.some((query) => query.getObserversCount() > 0);

    if (hasObservers || forceRefresh) {
      await queryClient.invalidateQueries({
        queryKey,
        refetchType: "active",
        staleTime,
      });
    } else {
      // Just mark as stale without refetching
      queryClient.invalidateQueries({
        queryKey,
        refetchType: "none",
      });
    }
  }
}

export default SyncService;
